import { ReactNode, useEffect, useState } from "react";

import { Alert, Button, Drawer, Popover, Row, Tag } from "@narmi/design_system";
import CopyableCell from "@components/CopyableCell";

import styles from "./table.module.css";
import { FEDNOW_TRANSACTION_STATES } from "./codesets";
import LabeledContentRow from "./LabeledContentRow";
import { moneyFormatter, Payment } from "./PaymentTable";
import { dateOptions } from "./PaymentDetailDrawer";
import { useInstitutionContext } from "@hooks/institutionContext";
import { HistoryRow } from "./CaseHistoryList";
import { AdminApiSchema } from "../../api_utils";
import { AlloyEvaluationModal } from "@components/alerts/alloy_evaluations/AlloyEvaluationModal";
import { AlloyEvaluationType } from "@components/alerts/alloy_evaluations/AlloyEvaluationSummary";
import DeclineOverrideDialog from "./DeclineOverrideDialog";
import RelatedPayments from "./RelatedPayments";
import { useUserConfiguration } from "@hooks/useUserConfiguration";

type FedNowTransaction = AdminApiSchema.components["schemas"]["FedNowTransactionAdmin"];
type HistoryItem = AdminApiSchema.components["schemas"]["StateChangeAudit"];

export function evaluationForMeta(metadata: Record<string, any>): AlloyEvaluationType | null {
  // DEPRECATED: Format for associating alloy evals has changed to FedNowTransaction.alloy_evaluation
  // Functionality preserved for backwards compatibility.
  const data = metadata.alloy_ongoing_evaluation_response?.event?.data;
  const evaluation = metadata.alloy_ongoing_evaluation_response?.event?.evaluations[0];
  if (!data || !evaluation) {
    return null;
  }
  return {
    evaluation_token: evaluation?.evaluation_token,
    entity_token: data?.entity_token,
    tags: evaluation?.summary?.tags || [],
    outcome: evaluation?.summary?.outcome,
    outcome_reasons: evaluation?.summary?.outcome_reasons || [],
  } as AlloyEvaluationType;
}

export default function FedNowTransactionDrawer({
  isOpen,
  setIsOpen,
  transaction,
  refresh,
  onNext,
  onPrev,
}: {
  isOpen: boolean;
  setIsOpen: CallableFunction;
  transaction: FedNowTransaction | null;
  refresh: CallableFunction;
  onNext?: (() => void) | null;
  onPrev?: (() => void) | null;
}) {
  const [morePopoverOpen, setMorePopoverOpen] = useState(false);
  const [evalModalOpen, setEvalModalOpen] = useState(false);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [relatedPayment, setRelatedPayment] = useState<Payment>();
  const [history, setHistory] = useState<Array<HistoryItem>>([]);
  const { institution } = useInstitutionContext();

  const { features } = useUserConfiguration();

  useEffect(() => {
    getTransactionHistory();
    getRelatedPayment();
  }, [transaction]);

  if (!transaction) {
    return null;
  }

  async function getTransactionHistory() {
    if (!transaction) {
      return;
    }
    const res = await fetch(`/v2/admin/transfers/instant/${transaction.id}/history/`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (!res.ok) {
      return;
    }
    const data = await res.json();
    setHistory(data.results);
  }

  async function getRelatedPayment() {
    if (!transaction?.payment_identification) {
      return;
    }
    const lookupRes = await fetch(
      `/v1/fednow/payments/${encodeURIComponent(transaction.payment_identification)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    if (!lookupRes.ok) {
      return;
    }
    const pmt = await lookupRes.json();
    setRelatedPayment(pmt);
  }

  function morePopoverOptions() {
    const buttons: Array<ReactNode> = [];
    if (transaction?.state === "declined") {
      buttons.push(
        <span
          className={`${styles.action} padding--all--xs`}
          onClick={() => {
            setMorePopoverOpen(false);
            setOverrideDialogOpen(true);
          }}
          key="override"
        >
          Override decline
        </span>
      );
    }
    return buttons;
  }

  const menuOptions = morePopoverOptions();
  let evaluation: AlloyEvaluationType | null;
  if ((transaction.metadata as any)?.alloy_ongoing_evaluation_response) {
    evaluation = evaluationForMeta(transaction.metadata as Record<string, any>);
  } else {
    evaluation = transaction.alloy_evaluations?.[0];
  }

  return (
    <Drawer
      depth="60%"
      isOpen={isOpen}
      showControls={onNext !== undefined || onPrev !== undefined}
      onNext={onNext ?? undefined}
      onPrev={onPrev ?? undefined}
      onUserDismiss={() => {
        setIsOpen(false);
        setEvalModalOpen(false);
      }}
    >
      <Row alignItems="center">
        <Row.Item>
          <h2 className="whiteSpace--truncate" style={{ maxWidth: "100%" }}>
            {transaction.user.first_name} {transaction.user.last_name}
          </h2>
        </Row.Item>
        <Row.Item shrink>
          <div style={{ textTransform: "capitalize" }}>
            <Tag label={transaction.state && FEDNOW_TRANSACTION_STATES[transaction.state]}></Tag>
          </div>
        </Row.Item>
        <Row.Item shrink>
          <div
            style={{
              float: "right",
              borderRight: menuOptions.length > 0 ? "2px solid lightGrey" : "",
            }}
            className={menuOptions.length > 0 ? "padding--right--s" : ""}
          >
            <CopyableCell
              copyText={`${window.location.origin}/fednow/declined/${transaction.id}`}
              text="Copy link"
              style={{ color: "var(--theme-primary)" }}
              buttonSize="s"
            />
          </div>
        </Row.Item>
        <Row.Item shrink>
          <div style={{ float: "right" }}>
            {menuOptions.length > 0 && (
              <Popover
                isOpen={morePopoverOpen}
                content={<div style={{ display: "table" }}>{menuOptions}</div>}
              >
                <Button
                  kind="plain"
                  startIcon="more-vertical"
                  onClick={() => setMorePopoverOpen(!morePopoverOpen)}
                />
              </Popover>
            )}
          </div>
        </Row.Item>
      </Row>
      <div className="padding--top">
        <Alert isDismissable={false} isActive={true} kind="warn" icon="alert-triangle">
          <span>Payment declined.</span>
          <span style={{ float: "right" }}>
            {new Date(transaction.updated_at).toLocaleString("en-US", dateOptions)}
          </span>
          <div className="padding--top--s">
            <Button kind="plain" label="View details" onClick={() => setEvalModalOpen(true)} />
          </div>
        </Alert>
      </div>
      <div>
        <div className={`${styles.transactionDetailContainer} margin--y padding--all`}>
          <h4 className="fontFamily--body padding--bottom">Payment details</h4>
          <LabeledContentRow label="Speed" content="Instant" />
          <LabeledContentRow
            label="Status"
            content={transaction.state && FEDNOW_TRANSACTION_STATES[transaction.state]}
            contentStyle={{ textTransform: "capitalize" }}
          />
          <LabeledContentRow
            label="Amount"
            content={moneyFormatter.format(transaction.amount_cents / 100)}
          />
          <LabeledContentRow label="Direction" content={"Outgoing"} />
          <LabeledContentRow label="Memo" content={transaction.memo || "-"} />
        </div>
        <div className={`${styles.sideBySideContainer}`}>
          <div className={`${styles.sideBySideChild} padding--all margin--right--l`}>
            <h4 className="fontFamily--body padding--bottom">Source</h4>
            <LabeledContentRow
              label="Sender"
              content={
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={
                    features.includes("customer_profile")
                      ? `/users/${transaction.user.id}`
                      : `/users/${transaction.user.id}/old/`
                  }
                >
                  {transaction.user.first_name} {transaction.user.last_name}
                </a>
              }
              contentStyle={{ textTransform: "capitalize" }}
              size="s"
            />
            <LabeledContentRow
              label="Account number"
              content={transaction.source_account.account_number || "-"}
              size="s"
            />
            <LabeledContentRow
              label="Routing number"
              content={`${institution.routing_number} (${institution.short_name || "Unknown institution"})`}
              size="s"
            />
          </div>
          <div className={`${styles.sideBySideChild} padding--all`}>
            <h4 className="fontFamily--body padding--bottom">Destination</h4>
            <LabeledContentRow
              label="Recipient"
              content={`${transaction.user.first_name} ${transaction.user.last_name}`}
              contentStyle={{ textTransform: "capitalize" }}
              size="s"
            />
            <LabeledContentRow
              label="Account number"
              content={
                transaction.external_account
                  ? transaction.external_account.account_number
                  : transaction.unverified_recipient.ach_destination.account_number
              }
              size="s"
            />
            <LabeledContentRow
              label="Routing number"
              content={
                transaction.external_account
                  ? `${transaction.external_account.routing_number} (${transaction.external_account.institution_name || "Unknown institution"})`
                  : `${transaction.unverified_recipient.ach_destination.routing_number} (${transaction.unverified_recipient.ach_destination.institution_name || "Unknown institution"})`
              }
              size="s"
            />
          </div>
        </div>
        <div className={`${styles.transactionDetailContainer} margin--y padding--all`}>
          <h4 className="fontFamily--body padding--bottom">Payment history</h4>
          {history &&
            history.map((item: HistoryItem) => {
              if (item.to_state === "pending") {
                return (
                  <HistoryRow
                    content={`${transaction.user.first_name} ${transaction.user.last_name} initiated payment.`}
                    timestamp={transaction.created_at}
                    rowType="no_additional"
                    kind="start"
                  />
                );
              }
              if (item.to_state === "declined") {
                return (
                  <HistoryRow
                    content="Payment was declined."
                    timestamp={item.timestamp}
                    rowType={evaluation ? "event" : "no_additional"}
                    kind="node"
                    onClickOverride={evaluation ? () => setEvalModalOpen(true) : undefined}
                  />
                );
              }
              if (item.to_state === "overridden") {
                return (
                  <HistoryRow
                    content={`${item.actor?.full_name} overrides decline`}
                    timestamp={item.timestamp}
                    rowType="event"
                    kind="node"
                    additional={transaction.override_note}
                  />
                );
              }
              return null;
            })}
        </div>
      </div>
      {relatedPayment && <RelatedPayments original={relatedPayment} includeOriginal />}
      {evaluation && (
        <AlloyEvaluationModal
          evaluation={evaluation}
          isModalOpen={evalModalOpen}
          setIsModalOpen={setEvalModalOpen}
        />
      )}
      <DeclineOverrideDialog
        isOpen={overrideDialogOpen}
        setIsOpen={setOverrideDialogOpen}
        transaction={transaction}
        successCallback={() => {
          refresh();
        }}
      />
    </Drawer>
  );
}
